<script setup lang="ts">
import ExampleBlock from '@/vue/molecules/components/helpers/example-block.vue';
import {useRequiredRule} from '@/ts/composables/pure/use-input-rules';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {useI18n} from 'vue-i18n';
import {computed} from 'vue';

const {t} = useI18n();

const props = defineProps<{
  name: string;
  exampleHtml?: string | null;
  descriptionHtml?: string | null;
}>();

const SIDE_BY_SIDE_MAX_TEXT_LENGTH = 200;
const isSideBySide = computed(() => {
  const textLength = props.exampleHtml?.replace(/<[^>]*>/gu, '').length ?? 0;
  return textLength > 0 && textLength < SIDE_BY_SIDE_MAX_TEXT_LENGTH;
});

const model = defineModel<string[]>();
const requiredRule = useRequiredRule();

function deleteItem(int: number): void {
  model.value!.splice(int, 1);
}
</script>
<template>
  <div class="example-multiple-input" :class="{sideBySide: isSideBySide}">
    <div class="left">
      <div class="instructions">
        <h5 class="title">
          {{ name }}
        </h5>
        <div v-if="descriptionHtml" v-safe-html="descriptionHtml" class="description"/>
      </div>
      <example-block v-if="exampleHtml" class="example" :example-html="exampleHtml"/>
    </div>
    <div class="right">
      <div class="inputs">
        <div v-for="(item, i) in model" :key="i" class="each-row">
          <flat-icon-btn
            v-if="(i + 1) === model!.length && i >= 1"
            class="trash"
            icon="trash.svg"
            @click="deleteItem(i)"
          />
          <q-input
            v-model="model![i]"
            :rules="[requiredRule]"
            type="textarea"
            borderless
            :placeholder="t('text_input_hint')"
            autogrow
          />
        </div>
      </div>
      <balm-btn
        theme="grey"
        :label="$t('add_item')"
        class="btn"
        icon="plus.svg"
        @click="model!.push('')"
      />
    </div>
  </div>
</template>
<style scoped lang="scss">
.example-multiple-input {
  @include q-card-white;
  padding: var(--Spacing-spacing-4);
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);

  @include screen-lg {
    padding: var(--Spacing-spacing-7);
  }
}

.left {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);

  .sideBySide & {
    @include screen-lg {
      flex-direction: row;
    }
  }
}

.instructions {
  flex: 1;
}

.example {
  .sideBySide & {
    @include screen-lg {
      flex: 1;
    }
  }
}

.title {
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-3);
}

.trash {
  position: absolute;
  width: 20px;
  z-index: 2;
  top: var(--Spacing-spacing-2);
  right: var(--Spacing-spacing-2);
  cursor: pointer;
}

.each-row {
  position: relative;
}

.right {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);
}

.inputs {
  @include q-input-textarea-10;
  gap: var(--Spacing-spacing-2);
  display: flex;
  flex-direction: column;
}

.btn {
  margin-left: auto;
}
</style>
